const BRAND = {
  bg: '#fdfcfb',
  panel: '#f2f0ec',
  accent: '#7a5c3e',
  text: '#3a352c',
  muted: '#8a8375',
};

const escapeHtml = val => {
  if (val === null || val === undefined || val === '') return '';
  return String(val).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
};

const row = (label, value) => {
  const v = escapeHtml(value);
  if (!v) return '';
  return `
    <tr>
      <td style="padding:6px 12px; font-size:13px; color:${BRAND.muted}; font-weight:600; width:180px; vertical-align:top;">${label}</td>
      <td style="padding:6px 12px; font-size:13px; color:${BRAND.text};">${v}</td>
    </tr>`;
};

const section = (title, rowsHtml) => {
  const filled = rowsHtml.filter(Boolean).join('');
  if (!filled) return '';
  return `
    <tr><td style="padding:0 0 20px 0;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background:${BRAND.panel}; border-radius:10px; overflow:hidden;">
        <tr><td style="padding:10px 16px; background:${BRAND.accent}; color:#fff; font-size:13px; font-weight:700; letter-spacing:.04em; text-transform:uppercase;">${escapeHtml(title)}</td></tr>
        <tr><td style="padding:4px 0;"><table width="100%" cellpadding="0" cellspacing="0">${filled}</table></td></tr>
      </table>
    </td></tr>`;
};

const checkedLabels = (fields = [], state = {}) =>
  fields.filter(f => state?.[f.name]).map(f => f.label || f.name).join(', ');

/** Builds a self-contained HTML email summarising a Timber Enquiry submission. */
export function buildEnquiryEmailHtml({
  state,
  isNewContact,
  hasExistingEnquiry,
  jobTypeIpFields = [],
  speciesIpFields = [],
  forestInfoIpFields = [],
  followUpSectionIpFields = [],
  officeUseSectionIpFields = [],
}) {
  const fullName = `${state.firstName || ''} ${state.lastName || ''}`.trim() || 'Unnamed contact';
  const heading = hasExistingEnquiry ? 'Timber Enquiry Updated' : 'New Timber Enquiry';

  const contactRows = [
    row('Name', fullName),
    row('Phone', state.phone),
    row('Email', state.email),
    row('Company', state.companyName),
    row('Contact Type', state.contactType),
    row('Address', [state.homeAddressLine1, state.homeAddressLine2, state.homeTown, state.homeCity, state.crmAddressCounty, state.homeCountry, state.homeZip].filter(Boolean).join(', ')),
  ];

  const propertyRows = [
    row('Site Address', [state.siteAddressLine1, state.siteAddressLine2, state.siteTown, state.siteCity, state.siteCountry, state.siteZip].filter(Boolean).join(', ')),
    row('Size (Hectares)', state.sizeInHectares),
    row('Forester Managing?', state.isForesterManaging === null ? '' : (state.isForesterManaging ? 'Yes' : 'No')),
    row('Forester Name', state.foresterName),
    row('Enquiry Originated From', state.enquiryOriginatedFrom),
    row('Forest Description', state.forestDescription),
    row('Has Felling Licence?', state.hasFellingLicense === null ? '' : (state.hasFellingLicense ? 'Yes' : 'No')),
    row('Felling Licence Number', state.fellingLicenseNumber),
    row('Job Type', checkedLabels(jobTypeIpFields, state.jobType)),
    row('Species', checkedLabels(speciesIpFields, state.species)),
    row('Map Location', state.dropPinUrl),
    ...forestInfoIpFields.map(f => row(f.label || f.name, state[f.name])),
  ];

  const enquiryRows = [
    row('Type of Enquiry', state.typeOfEnquiry),
    row('Transaction Type', state.transactionType),
    row('Enquiry Type', state.enquiryType),
    row('Send Brochure?', state.sendBrochure ? 'Yes' : 'No'),
    row('Brochure Note', state.brochureNote),
    row('Additional Info From Grower', state.additionalInfoFromGrower),
  ];

  const followUpRows = [
    row('Follow Up Details', state.followUpDetails),
    row('Follow Up Date', state.followUpDate),
    row('Allocated To', state.allocatedTo),
    row('Pipeline Status', state.pipelineStatus),
    row('Enquiry Rating', state.enquiryRating),
    ...followUpSectionIpFields.map(f => row(f.label || f.name, state[f.name])),
  ];

  const officeRows = officeUseSectionIpFields.map(f => row(f.label || f.name, state[f.name]));

  const metaRows = [
    row('Team', state.crmTeamId),
    row('Stage', state.crmStageName),
    row('Sales Rep', state.crmSalesRep),
    row('Completed By', state.completedBy),
    row('Contact Status', isNewContact ? 'New Contact' : 'Existing Contact'),
  ];

  return `
  <div style="font-family:Segoe UI, Arial, sans-serif; background:${BRAND.bg}; padding:24px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="max-width:640px; margin:0 auto;">
      <tr><td style="padding-bottom:16px;">
        <div style="font-size:20px; font-weight:700; color:${BRAND.text};">${escapeHtml(heading)}</div>
        <div style="font-size:13px; color:${BRAND.muted}; margin-top:2px;">${escapeHtml(fullName)} &middot; ${escapeHtml(new Date().toLocaleString('en-GB'))}</div>
      </td></tr>
      ${section('Contact Details', contactRows)}
      ${section('Property & Forest', propertyRows)}
      ${section('Enquiry Details', enquiryRows)}
      ${section('Follow Up', followUpRows)}
      ${section('Office Use', officeRows)}
      ${section('Submission Info', metaRows)}
    </table>
  </div>`;
}

export function buildEnquiryEmailSubject({ state, hasExistingEnquiry }) {
  const fullName = `${state.firstName || ''} ${state.lastName || ''}`.trim() || 'Unnamed contact';
  return hasExistingEnquiry ? `Timber Enquiry Updated — ${fullName}` : `New Timber Enquiry — ${fullName}`;
}